<script setup>
import { i18n } from 'boot/i18n';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrIconActionBtn,
  HrListSelect,
  HrSettingCheckbox,
  HrTextField,
} from 'src/design-system';
import { computed, ref } from 'vue';

import { POLICYHOLDER_RELATIONSHIP, POLICYHOLDER_RELATIONSHIP_VALUES } from '../../domain/constants/coverage';
import { COVERAGE_ERRORS, validateCoverage } from '../../domain/model/Coverage';
import { createPolicyholder } from '../../domain/model/Policyholder';
import { POLICYHOLDER_READINESS, policyholderReadiness } from '../../domain/services/policyholderReadiness';
import { usePersonCandidates } from '../composables/usePersonCandidates';
import { toPersonPickerRows } from '../presenters/personPickerRows';
import AddPolicyholderDialog from './AddPolicyholderDialog.vue';
import CalendarDateField from './CalendarDateField.vue';
import PersonPicker from './PersonPicker.vue';

/**
 * Add or edit one coverage — the client's relationship to a single funding source.
 *
 * Validation is the domain's: the form collects, `validateCoverage` decides. Field rules here
 * only surface the resulting error codes in the right place.
 *
 * PAUSING BILLING IS NOT HERE. It lives on the row's ⋮ menu, and the reason is UX rather than
 * architecture: a form that reads "Eligibility verified — on" next to "Pause billing — on" asks
 * the reader to hold two switches pointing in opposite directions, one affirming the coverage and
 * one withholding it. Pausing is also rare next to the fields around it. A menu action reads as
 * something done TO a coverage, which is what it is; the form stays the coverage's own facts.
 *
 * Nothing model-side forced the choice — the unit of work stages the two aggregates
 * independently, so this form could gather both and emit both. It simply reads better not to.
 *
 * The draft is a FLAT form object, not a Coverage. The entity holds its effective bounds in an
 * `EffectivePeriod` value object, which is frozen and has to be replaced rather than edited — the
 * right shape for a domain invariant and the wrong one for two `v-model`s. So the dialog flattens
 * on the way in, edits primitives, and emits primitives; the unit of work builds the entity. That
 * is also what lets `validateCoverage` see the bounds RAW, which is the only moment an unreadable
 * date is still distinguishable from a deliberately open one.
 */
defineOptions({ name: 'EditCoverageDialog' });

const props = defineProps({
  /** The coverage being edited; a blank one when adding. */
  coverage: {
    type: Object,
    required: true,
  },
  /** Selectable funding sources. @type {import('../../domain/model/FundingSource.types').FundingSource[]} */
  fundingSources: {
    type: Array,
    default: () => [],
  },
  /** The client's other coverages — the same-payer overlap check needs them. */
  siblings: {
    type: Array,
    default: () => [],
  },
  /**
   * The client as they currently stand in the editor this dialog opened from, unsaved edits
   * included. Null when the host has nothing live to offer, which falls back to what was read.
   * @type {import('../../domain/model/PolicyholderDemographics').PolicyholderDemographics|null}
   */
  clientDemographics: {
    type: Object,
    default: null,
  },
  /** The clinic's country, which shapes the address fields when recording a new policyholder. */
  clinicCountry: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** The edited coverage, as flat form attributes. */
  ok: null,
  /**
   * Corrections to the CLIENT's own record, for the host to merge into the draft it is holding.
   *
   * Not written here, and that is the whole design. The client's document is already being edited by
   * the screen this dialog opened from, so a write from here would be a second writer against it and
   * the outer save would put its own stale copy back over the top. Handing the change to the one
   * holder means there is still exactly one, and it saves with everything else on that form.
   */
  'update-client': null,
  /** Recording or correcting a person failed. The host owns telling somebody. */
  error: null,
  hide: null,
});

const dialog = ref(null);

/** A Coverage as editable primitives: each value object spread back out into bindable fields. */
const toDraft = (coverage) => {
  const { period, policyholder, ...rest } = coverage;

  return {
    ...rest,
    effectiveStart: period?.start ?? null,
    effectiveEnd: period?.end ?? null,
    policyholderId: policyholder?.personId ?? null,
    policyholderRelationship: policyholder?.relationship ?? null,
    // The fact itself stays on the draft so the factory can tell an unchanged verdict from a new
    // check; the switch edits this flat copy of it.
    eligibilityVerified: coverage.eligibilityVerification?.verified === true,
  };
};

const draft = ref(toDraft(props.coverage));

const isAdding = computed(() => {
  return !props.siblings.some((sibling) => sibling.id === props.coverage.id);
});

const title = computed(() => {
  return isAdding.value
    ? i18n.t('coverage.editCoverage.addTitle')
    : i18n.t('coverage.editCoverage.editTitle');
});

const fundingSourceOptions = computed(() => {
  // The option's value is the PAYER, because that is what a coverage stores. A version's own
  // document id would be a different string for every version but the first.
  return props.fundingSources.map((fundingSource) => ({
    label: fundingSource.name,
    value: fundingSource.fundingSourceId,
  }));
});

const picker = ref(null);

const {
  suggested,
  others,
  isLoadingSuggested,
  isLoadingClinicPeople,
  loadNamed,
  loadSuggested,
  loadClinicPeople,
  createPerson,
  editPerson,
} = usePersonCandidates();

const addDialog = ref(null);

/**
 * Who the person form is currently correcting, or null when it is recording somebody new.
 *
 * Kept here as well as handed to `show()`, because the submit handler needs to know who came back
 * and the form only ever returns a profile.
 *
 * THE CLIENT IS ALLOWED, and reaches a different destination. One affordance for every policyholder
 * is the point: a reader who has just been told what is missing should not find that whether they
 * can fix it here depends on who holds the policy, and sending them to another tab would close this
 * dialog and lose the coverage they were part-way through. What differs is invisible and belongs
 * that way — a person we own is written immediately, while the client goes back to the draft the
 * host is holding, because that record already has an editor open on it.
 */
const editingPerson = ref(null);

const isEditingClient = computed(() => {
  return editingPerson.value?.personId === props.coverage.clientId;
});

/**
 * The person currently named, kept beside the draft so the field can show a name rather than an id.
 *
 * Resolved from the candidate sets rather than read separately: somebody chosen in this session came
 * from one of those lists, and one already on the coverage is named to `loadSuggested` so that it
 * lands there too.
 */
const namedPerson = computed(() => {
  if (draft.value.policyholderId === null) {
    return null;
  }

  return [...suggested.value, ...others.value]
    .find(({ personId }) => personId === draft.value.policyholderId) ?? null;
});

/**
 * Whether the field is waiting on somebody it has already named.
 *
 * Blank is not a loading state: on a read-only field it reads as "nobody holds this policy", which
 * is the same wrong statement the unresolved wording exists to avoid, just earlier. Narrow on
 * purpose — a coverage the client holds has its answer without reading anybody, and a widening read
 * behind the picker must not put a spinner on a field that already shows the right name.
 */
const isResolvingPolicyholder = computed(() => isLoadingSuggested.value
  && draft.value.policyholderId !== null
  && namedPerson.value === null);

const isAnswered = computed(() => draft.value.policyholderRelationship !== null);
const isSelf = computed(
  () => draft.value.policyholderRelationship === POLICYHOLDER_RELATIONSHIP.SELF,
);

/**
 * Blank until somebody says who pays, rather than pre-filled with the client.
 *
 * In ABA the client is usually a child and the policyholder usually a parent, so offering the client
 * as the resting answer would make the less likely one the answer nobody has to touch. The picker
 * still offers them first, which is where the convenience belongs.
 */
const policyholderLabel = computed(() => {
  if (!isAnswered.value && draft.value.policyholderId === null) {
    return '';
  }

  if (isSelf.value) {
    return i18n.t('coverage.policyholderRelationship.self');
  }

  if (namedPerson.value === null) {
    // STILL ARRIVING IS NOT UNREADABLE. The candidate sets are empty until the read opening this
    // dialog lands, so every coverage that names somebody would state the problem below and then
    // withdraw it a moment later. Same rule as `policyholderReadiness`, which stays quiet while the
    // demographics are unknown rather than flashing every requirement at once.
    if (isLoadingSuggested.value) {
      return '';
    }

    // A named person we cannot resolve: the id is real, the record is not readable. Falling back to
    // a blank would say "no policyholder", which is a different and wrong statement, and the field
    // is read-only so there is nothing to distinguish it from an unanswered one.
    return i18n.t('coverage.editCoverage.policyholderUnresolved');
  }

  // The same fallback the picker uses. A person can be on file with no name part at all, since the
  // only floor on recording one is a single name and older records may have neither, and an empty
  // label here would read as "no policyholder" on a field that is read-only.
  return namedPerson.value.fullName
    || [namedPerson.value.firstName, namedPerson.value.lastName].filter(Boolean).join(' ')
    || i18n.t('personPicker.unnamed');
});

const relationshipOptions = POLICYHOLDER_RELATIONSHIP_VALUES
  .filter((value) => value !== POLICYHOLDER_RELATIONSHIP.SELF)
  .map((value) => ({ label: i18n.t(`coverage.policyholderRelationship.${value}`), value }));

/**
 * The client, preferring what the editor behind this dialog is holding over what was read.
 *
 * An address typed into the client's own form and not yet saved is still the truth about that
 * client, and readiness that ignores it accuses somebody of a gap they just filled.
 */
const clientOnFile = computed(() => {
  return props.clientDemographics
    ?? suggested.value.find(({ personId }) => personId === props.coverage.clientId)
    ?? null;
});

/**
 * Whose record the correction affordance opens on: the client when they hold their own policy,
 * whoever is named otherwise, and nobody until the question has been answered.
 */
const editablePerson = computed(() => (isSelf.value ? clientOnFile.value : namedPerson.value));

const pickerContext = computed(() => ({ subjectId: props.coverage.clientId }));

// Same substitution in the list somebody chooses from, so the row for the client does not contradict
// the form it was read from.
const suggestedRows = computed(() => {
  const people = props.clientDemographics === null
    ? suggested.value
    : suggested.value.map((person) => (
      person.personId === props.coverage.clientId ? props.clientDemographics : person
    ));

  return toPersonPickerRows(people, pickerContext.value);
});
const otherRows = computed(() => toPersonPickerRows(others.value, pickerContext.value));

/**
 * Whoever holds the policy, as demographics. The client when the answer is self, the named person
 * otherwise, and nothing while the answer is still missing or the record has not arrived.
 */
const policyholderDemographics = computed(() => {
  if (isSelf.value) {
    return clientOnFile.value;
  }

  return namedPerson.value;
});

/**
 * What a payer still wants before this coverage can bill.
 *
 * NON-BLOCKING BY CONSTRUCTION: it is read here and shown, and it never reaches `errors`. Coverage
 * is recorded from a card that usually carries a name and a member id and little else, and refusing
 * the save would produce placeholder birthdays, which nothing downstream can tell from real ones.
 *
 * Named specifically rather than as a count, because "three fields missing" sends somebody hunting
 * and "a date of birth and an address" sends them to the card in their hand.
 */
const missingForBilling = computed(() => {
  // Built rather than handed the draft. The draft is flat, so its relationship lives under
  // `policyholderRelationship`, and passing it straight through leaves the rule reading `relationship`
  // off an object that has no such field: every coverage then looks like a non-self one and the
  // client is never asked for the gender and address a payer wants from them.
  return policyholderReadiness(
    {
      policyholder: createPolicyholder({
        personId: draft.value.policyholderId,
        relationship: draft.value.policyholderRelationship,
      }),
      memberId: draft.value.memberId,
    },
    policyholderDemographics.value,
  );
});

/**
 * Reported under the field that can fix it, which is not one field.
 *
 * Readiness spans two records: the person's details and the member id, which is a fact about the
 * policy rather than about whoever holds it and has its own input a few rows down. Naming it under
 * the policyholder sent somebody to a person picker to supply a number they were looking straight at.
 */
const readinessNotice = computed(() => {
  const missing = missingForBilling.value
    .filter((code) => code !== POLICYHOLDER_READINESS.MISSING_MEMBER_ID);

  if (missing.length === 0) {
    return null;
  }

  return i18n.t('coverage.readiness.notice', {
    fields: missing.map((code) => i18n.t(`coverage.readiness.${code}`)).join(', '),
  });
});

const isMemberIdNeeded = computed(() => {
  return missingForBilling.value.includes(POLICYHOLDER_READINESS.MISSING_MEMBER_ID);
});

const errors = computed(() => validateCoverage(draft.value, props.siblings));

const has = (code) => errors.value.includes(code);

const fundingSourceRules = [
  () => !has(COVERAGE_ERRORS.FUNDING_SOURCE_REQUIRED)
    || i18n.t('coverage.editCoverage.inputFundingSourceError'),
  () => !has(COVERAGE_ERRORS.DUPLICATE_OVERLAPPING_FUNDING_SOURCE)
    || i18n.t('coverage.editCoverage.inputDuplicateFundingSourceError'),
];

// UNREADABLE_EFFECTIVE_DATE means a date field handed the entity something that is not a calendar
// date. It cannot happen through the current picker; it is the guard against a date input that
// speaks another type being swapped in. Surfaced on both fields since either can carry it.
const unreadableDateRule = () => !has(COVERAGE_ERRORS.UNREADABLE_EFFECTIVE_DATE)
  || i18n.t('coverage.editCoverage.inputUnreadableDateError');

const policyholderRules = [
  () => !has(COVERAGE_ERRORS.POLICYHOLDER_REQUIRED)
    || i18n.t('coverage.editCoverage.inputPolicyholderError'),
  () => !has(COVERAGE_ERRORS.POLICYHOLDER_PERSON_REQUIRED)
    || i18n.t('coverage.editCoverage.inputPolicyholderError'),
  // Also reported here, not only on the relationship, because that select is hidden whenever nobody
  // is named. A coverage saved by a newer version with a relationship this one does not know would
  // otherwise disable Save with nothing on screen saying why, which is the shape of a bug report
  // nobody can act on.
  () => !has(COVERAGE_ERRORS.UNSUPPORTED_POLICYHOLDER)
    || i18n.t('coverage.editCoverage.inputPolicyholderUnsupported'),
];

const relationshipRules = [
  () => !has(COVERAGE_ERRORS.POLICYHOLDER_RELATIONSHIP_REQUIRED)
    || i18n.t('coverage.editCoverage.inputRelationshipError'),
  () => !has(COVERAGE_ERRORS.UNSUPPORTED_POLICYHOLDER)
    || i18n.t('coverage.editCoverage.inputRelationshipError'),
];

const effectiveStartRules = [
  () => !has(COVERAGE_ERRORS.EFFECTIVE_START_REQUIRED)
    || i18n.t('coverage.editCoverage.inputEffectiveStartError'),
  unreadableDateRule,
];

const effectiveEndRules = [
  () => !has(COVERAGE_ERRORS.EFFECTIVE_END_BEFORE_START)
    || i18n.t('coverage.editCoverage.inputEffectiveEndError'),
  unreadableDateRule,
];

/**
 * Whether the candidate list behind the picker has been read for this opening.
 *
 * A FAILED read is not remembered, so the next attempt tries again rather than leaving the picker
 * permanently empty — the same rule `loadClinicPeople` follows for the clinic set.
 */
const hasLoadedCandidates = ref(false);

/**
 * The named read, held so the picker can wait for it.
 *
 * Both reads fill the same set, and the wider one has to land last or it is overwritten by the
 * narrower. Opening the picker before the dialog's own read returns is easy enough to do — it is one
 * click on a field that is already on screen — and the result would be a picker stuck at two names
 * for as long as it stayed open.
 */
const namedRead = ref(null);

/**
 * Opens with only the people needed to display the named policyholder.
 * The full candidate list is deferred until the picker opens.
 */
function show() {
  dialog.value?.show();
  hasLoadedCandidates.value = false;
  // ONLY WHO THE FORM NAMES. The field has to show the policyholder, so that person is resolved on
  // open; everyone who might have been chosen instead is the picker's question, and waits for it.
  // Reported rather than swallowed: a name we failed to read and a coverage naming nobody look
  // identical on a read-only field, and the second is a different and wrong statement.
  namedRead.value = loadNamed({
    clientId: props.coverage.clientId,
    clinicId: props.coverage.clinicId,
    personIds: [
      // The client ONLY when nobody upstream is holding them. The editor this dialog opens from is
      // usually holding that record already, and `clientOnFile` prefers it, so reading it back
      // would be a second copy of what is on screen.
      props.clientDemographics === null ? props.coverage.clientId : null,
      // Whoever the coverage already names, who is otherwise resolvable only by luck: a policyholder
      // with no login is neither the client nor connected to them, and the clinic set is not read
      // until somebody searches.
      draft.value.policyholderId,
    ],
  });
  namedRead.value.catch((error) => emit('error', error));
} // show()

/**
 * Opens the picker, reading its candidates the first time it is asked for.
 *
 * The list is the client's whole access list, so its cost scales with the size of the care team.
 * On the commonest coverage — the client holds their own policy — nobody ever opens this, and that
 * read used to happen anyway on every edit.
 */
async function openPicker() {
  picker.value?.show();

  if (hasLoadedCandidates.value) {
    return;
  }
  hasLoadedCandidates.value = true;

  try {
    // Settled, not rethrown: a failed named read is already reported by `show`, and reporting it a
    // second time here would blame the picker for it.
    await namedRead.value?.catch(() => {});
    await loadSuggested({
      clientId: props.coverage.clientId,
      clinicId: props.coverage.clinicId,
      alsoInclude: [draft.value.policyholderId],
    });
  } catch (error) {
    hasLoadedCandidates.value = false;
    emit('error', error);
  }
} // openPicker()

/**
 * Choosing the client themselves is choosing self, not choosing a person who happens to be them.
 * Storing their id would be a second copy of something already known, and would make every
 * self-coverage answer yes to "is this person somebody's policyholder".
 */
function onPersonSelected(row) {
  const isSubject = row.id === props.coverage.clientId;

  draft.value.policyholderId = isSubject ? null : row.id;
  draft.value.policyholderRelationship = isSubject
    ? POLICYHOLDER_RELATIONSHIP.SELF
    // Left unset rather than guessed. What somebody is to the client is not derivable from having
    // been chosen, and a default here would be recorded as an answer nobody gave.
    : null;
} // onPersonSelected()

/**
 * A person recorded here is chosen immediately. Nobody adds one in order to then go looking for
 * them, and making them re-find somebody they just described would be the flow doubling back.
 */
async function onPersonSubmitted(profile) {
  if (isEditingClient.value) {
    emit('update-client', profile);
    return;
  }

  if (editingPerson.value !== null) {
    const corrected = await editPerson(editingPerson.value.personId, profile);

    // The write landed and the read back did not, so what is on screen is now behind the record.
    // Silence here would leave readiness reporting a gap somebody has just filled.
    if (corrected === null) {
      emit('error', new Error('The person was updated but could not be read back'));
    }

    return;
  }

  // Chosen by id rather than by what came back. The person exists either way, and a failed read is
  // no reason to leave the coverage naming nobody: retrying would record a second one, and this
  // read failing is more likely to be a rule than a blip, so every retry would do it again.
  const { personId, person } = await createPerson(profile);

  onPersonSelected({ id: personId });

  if (person === null) {
    emit('error', new Error('The person was recorded but could not be read back'));
  }
} // onPersonSubmitted()

/**
 * The picker reports what was typed rather than opening anything itself, because creating a
 * policyholder is this context's concern and the picker is general.
 */
function onCreateRequested(name) {
  editingPerson.value = null;
  addDialog.value?.show({ typedName: name, save: onPersonSubmitted });
} // onCreateRequested()

/**
 * The wider set is read on demand, and it can fail like any read.
 *
 * Surfaced rather than left to reject on its own: the picker would otherwise sit on an empty list
 * that says nobody matched, which is the one answer that stops somebody looking further.
 */
function onSearchRequested() {
  loadClinicPeople().catch((error) => emit('error', error));
} // onSearchRequested()

/**
 * Completing what readiness reports, without leaving the coverage half-recorded.
 *
 * Readiness names a gap on a record, so without somewhere to fix it the notice is a standing
 * accusation: the one screen that knows what is missing would be the one screen that cannot do
 * anything about it, and the coverage would have to be abandoned to go and find the record.
 */
function onEditRequested() {
  editingPerson.value = editablePerson.value;
  addDialog.value?.show({ person: editablePerson.value, save: onPersonSubmitted });
} // onEditRequested()

function hide() {
  dialog.value?.hide();
} // hide()

/**
 * Left enabled while the form is invalid, which is the only way the reader is ever told why.
 *
 * The rules below hold every message, and the form runs them on submit and refuses to emit until
 * they pass. A Save disabled on `errors` never lets that happen: the click lands on nothing, no
 * rule runs, and a missing relationship reads as a dead button.
 *
 * The check stays as a backstop for an error code no field rule surfaces, which is what a code
 * added to the entity without a matching rule would be.
 */
function onSubmit() {
  if (errors.value.length > 0) {
    return;
  }
  emit('ok', draft.value);
  hide();
} // onSubmit()

defineExpose({ show, hide });
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    size="sm"
    :title="title"
    data-e2e="dialog-edit-coverage"
    @submit="onSubmit"
    @hide="emit('hide')"
  >
    <div class="row q-col-gutter-md">
      <hr-list-select
        v-model="draft.fundingSourceId"
        class="col-12"
        :label="i18n.t('coverage.fields.fundingSource')"
        :options="fundingSourceOptions"
        :rules="fundingSourceRules"
        hide-bottom-space
        data-e2e="select-coverage-funding-source"
      />

      <!-- Readonly rather than disabled: the value is chosen elsewhere, but the field is very much
           live. A disabled field is what taught people the policyholder was decided for them, and
           the trailing icon plus the pointer are what say otherwise.

           KEYBOARD AS WELL AS POINTER. A readonly input takes focus but types nothing, so Enter
           would reach the surrounding form and submit it while Space did nothing. This field is the
           only way into the picker, so without the keydown handlers the whole task needs a mouse. -->
      <hr-text-field
        class="col-12 person-field"
        :model-value="policyholderLabel"
        :label="i18n.t('coverage.fields.policyholder')"
        :placeholder="i18n.t('coverage.editCoverage.policyholderPlaceholder')"
        :rules="policyholderRules"
        :loading="isResolvingPolicyholder"
        readonly
        data-e2e="input-coverage-policyholder"
        @click="openPicker"
        @keydown.enter.prevent="openPicker"
        @keydown.space.prevent="openPicker"
      >
        <template #append>
          <!-- Stopped, because the field itself opens the picker and correcting somebody is not
               choosing somebody else. -->
          <hr-icon-action-btn
            v-if="editablePerson !== null"
            icon="edit"
            size="sm"
            :aria-label="i18n.t('coverage.editCoverage.editPolicyholderLabel')"
            data-e2e="button-coverage-edit-policyholder"
            @click.stop="onEditRequested"
          />
          <!-- A button rather than a plain icon. Quasar binds the field's own listeners to the
               native input, so a click anywhere in this slot never reaches the handler that opens
               the picker, and an icon that looks pressable but is not is worse than none. -->
          <hr-icon-action-btn
            icon="search"
            size="sm"
            :aria-label="i18n.t('coverage.editCoverage.selectPolicyholderTitle')"
            data-e2e="button-coverage-select-policyholder"
            @click="openPicker"
          />
        </template>

        <!-- Under the field rather than in a banner, because it is a fact about this answer and the
             field is where you go to change it. Warning rather than error: the row says the same
             condition in the same colour, and error styling would promise the save is blocked when
             it deliberately is not. -->
        <template #hint>
          <span
            v-if="readinessNotice !== null"
            class="hr-text-warning"
            data-e2e="hint-coverage-readiness"
          >{{ readinessNotice }}</span>
          <span v-else-if="isSelf">
            {{ i18n.t('coverage.editCoverage.policyholderSelfHint') }}
          </span>
        </template>
      </hr-text-field>

      <!-- Only once somebody other than the client holds the policy. Asked here rather than in the
           picker, which is a general component and knows nothing about policyholders. -->
      <hr-list-select
        v-if="draft.policyholderId !== null"
        v-model="draft.policyholderRelationship"
        class="col-12"
        :label="i18n.t('coverage.fields.relationship')"
        :options="relationshipOptions"
        :rules="relationshipRules"
        hide-bottom-space
        data-e2e="select-coverage-relationship"
      />

      <hr-text-field
        v-model="draft.memberId"
        class="col-12 col-sm-6"
        :label="i18n.t('coverage.fields.memberId')"
        :hide-bottom-space="!isMemberIdNeeded"
        data-e2e="input-coverage-member-id"
      >
        <template #hint>
          <span
            v-if="isMemberIdNeeded"
            class="hr-text-warning"
            data-e2e="hint-coverage-member-id-readiness"
          >{{ i18n.t('coverage.readiness.memberIdNotice') }}</span>
        </template>
      </hr-text-field>

      <hr-text-field
        v-model="draft.groupNumber"
        class="col-12 col-sm-6"
        :label="i18n.t('coverage.fields.groupNumber')"
        hide-bottom-space
      />

      <calendar-date-field
        v-model="draft.effectiveStart"
        class="col-12 col-sm-6"
        :label="i18n.t('coverage.fields.effectiveStart')"
        :rules="effectiveStartRules"
        data-e2e="input-coverage-effective-start"
      />

      <calendar-date-field
        v-model="draft.effectiveEnd"
        class="col-12 col-sm-6"
        :label="i18n.t('coverage.fields.effectiveEnd')"
        :hint="i18n.t('coverage.editCoverage.effectiveEndHint')"
        :rules="effectiveEndRules"
        clearable
      />

      <!-- Two independent decisions, not one status: a coverage can be verified and on hold.
           Notes come last so growing that field never pushes these below the fold.

           A CHECKBOX RATHER THAN A SWITCH because this dialog saves on confirm. A switch states
           what is true right now, so one that only becomes true on Save reads as already done. -->
      <hr-setting-checkbox
        v-model="draft.eligibilityVerified"
        :label="i18n.t('coverage.fields.eligibilityVerified')"
        :hint="i18n.t('coverage.fields.eligibilityVerifiedHint')"
        class="col-12"
        data-e2e="checkbox-coverage-eligibility-verified"
      />

      <hr-text-field
        v-model="draft.notes"
        class="col-12"
        :label="i18n.t('coverage.fields.notes')"
        hide-bottom-space
      />
    </div>

    <person-picker
      ref="picker"
      :title="i18n.t('coverage.editCoverage.selectPolicyholderTitle')"
      :suggested="suggestedRows"
      :others="otherRows"
      :is-loading-suggested="isLoadingSuggested"
      :is-loading-others="isLoadingClinicPeople"
      @search="onSearchRequested"
      @select="onPersonSelected"
      @create="onCreateRequested"
    />

    <add-policyholder-dialog
      ref="addDialog"
      :country="clinicCountry"
      @error="emit('error', $event)"
    />

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="i18n.t('common.buttonSave')"
        type="submit"
        data-e2e="btn-save-coverage"
      />
    </template>
  </hr-form-dialog>
</template>

<style lang="scss" scoped>

/* The field opens a picker, so it has to read as something you press. */
.person-field :deep(.q-field__native) {
  cursor: pointer;
}
</style>
